import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sendOtp, loginUser, loginTechnician } from '../api/api';
import eventBus from "../eventBus";

const getAuthenticatedDestination = (search) => {
  const hasSession = (tokenKey, infoKey) => {
    const info = localStorage.getItem(infoKey);
    const token = localStorage.getItem(tokenKey) || (info && localStorage.getItem('auth_token'));

    if (!token || !info) return false;

    try {
      return Boolean(JSON.parse(info));
    } catch {
      return false;
    }
  };

  const hasUserSession = hasSession('user_token', 'user_info');
  const hasTechnicianSession = hasSession('technician_token', 'technician_info');

  if (hasUserSession && hasTechnicianSession) {
    return new URLSearchParams(search).get('tab') === 'tech' ? '/tech-dashboard' : '/profile';
  }
  if (hasTechnicianSession) return '/tech-dashboard';
  if (hasUserSession) return '/profile';
  return null;
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [authenticatedDestination] = useState(() => getAuthenticatedDestination(location.search));
  const [activeTab, setActiveTab] = useState(() => (
    new URLSearchParams(location.search).get('tab') === 'tech' ? 'tech' : 'user'
  ));
  const [userStep, setUserStep] = useState('phone');
  const [techStep, setTechStep] = useState('phone');
  const [userOtp, setUserOtp] = useState(['', '', '', '']);
  const [techOtp, setTechOtp] = useState(['', '', '', '']);
  const [statusMessage, setStatusMessage] = useState('');
  const [apiError, setApiError] = useState('');

  // Shared state for the phone number input
  const [mobileNumber, setMobileNumber] = useState('');

  const userInputRefs = useRef([]);
  const techInputRefs = useRef([]);

  useEffect(() => {
    if (authenticatedDestination) {
      navigate(authenticatedDestination, { replace: true });
    }
  }, [authenticatedDestination, navigate]);

  useEffect(() => {
    if (activeTab === 'user' && userStep === 'otp') {
      userInputRefs.current[0]?.focus();
    }
    if (activeTab === 'tech' && techStep === 'otp') {
      techInputRefs.current[0]?.focus();
    }
  }, [activeTab, userStep, techStep]);

  // Clean the state value directly instead of checking refs
  const getPhoneValue = () => {
    return mobileNumber.trim().replace(/\D/g, '');
  };

  const handleSendOtp = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }

    console.log('Initiating OTP send...');
    const type = activeTab;
    console.log(`Active tab: ${type}`);

    const mobile = getPhoneValue();
    console.log(`Raw mobile input: "${mobileNumber}"`);

    // Dynamic requester assignment based on your rules
    const requester = type === 'tech' ? 'Technician' : 'User';
    console.log(`Requester designated as: ${requester}`);
    console.log(`Cleaned mobile: "${mobile}" (length: ${mobile.length})`);

    if (!/^[0-9]{10}$/.test(mobile)) {
      setApiError('Please enter a valid 10-digit mobile number.');
      return;
    }

    try {
      setApiError('');
      setStatusMessage('Sending OTP...');
      const response = await sendOtp(mobile, requester);
      console.log('OTP response:', response);

      if (response.status === 200) {
        setStatusMessage(response.message || 'OTP sent successfully.');
        alert("your OTP is: " + response.otp);
        if (type === 'user') {
          setUserStep('otp');
          setUserOtp(['', '', '', '']);
        } else {
          setTechStep('otp');
          setTechOtp(['', '', '', '']);
        }
      } else {
        console.log("the response is not 200, it's:", response);
        setApiError(response.message || 'Unable to send OTP.');
        setStatusMessage('New user detected. Redirecting to registration...');
        setTimeout(() => {
          navigate('/register', {
            state: {
              mobile: mobile,
              requester: type === 'tech' ? 'Technician' : 'User',
            },
          });
        }, 1200);
        
      }
    } catch (error) {
      setApiError(error.message || 'Network error while sending OTP.');
      setStatusMessage('');
    }
  };

  const handleOtpChange = (index, value, type) => {
    if (!/^\d*$/.test(value)) return;
    if (value.length > 1) return;

    if (type === 'user') {
      const newOtp = [...userOtp];
      newOtp[index] = value;
      setUserOtp(newOtp);
      if (value && index < 3) {
        userInputRefs.current[index + 1]?.focus();
      }
    } else {
      const newOtp = [...techOtp];
      newOtp[index] = value;
      setTechOtp(newOtp);
      if (value && index < 3) {
        techInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index, type) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      if (type === 'user') {
        userInputRefs.current[index - 1]?.focus();
      } else {
        techInputRefs.current[index - 1]?.focus();
      }
    }
  };

  const handleVerifyOtp = async () => {
    const type = activeTab;
    const mobile = getPhoneValue();
    const otp = type === 'user' ? userOtp.join('') : techOtp.join('');
    const device_token = 'device_token_123';

    if (!/^[0-9]{4}$/.test(otp)) {
      setApiError('Please enter the 4‑digit OTP.');
      return false;
    }
    if (!/^[0-9]{10}$/.test(mobile)) {
      setApiError('Invalid mobile number.');
      return false;
    }

    try {
      setApiError('');
      setStatusMessage('Logging in...');

      const response = type === 'user'
        ? await loginUser(mobile, otp, device_token)
        : await loginTechnician(mobile, otp, device_token);

      console.log('Login response:', response);

// In handleVerifyOtp function, replace this part:

if (response.status === 200 && response.success === true) {
  const token = response.data?.token;
  if (token) {
    let userInfo;
    
    if (type === 'user') {
      localStorage.removeItem('technician_token');
      localStorage.removeItem('technician_info');
      localStorage.removeItem('auth_token');
      userInfo = {
        user_id: response.data.user_id,
        user_name: response.data.user_name,
        mobile: response.data.mobile,
        user_type: 'User',
      };
      localStorage.setItem('user_token', token);
      localStorage.setItem('user_info', JSON.stringify(userInfo));
    } else {
      localStorage.removeItem('user_token');
      localStorage.removeItem('user_info');
      localStorage.removeItem('auth_token');
      userInfo = {
        technician_id: response.data.technician_id,
        name: response.data.name,
        email: response.data.email,
        mobile: response.data.mobile,
        profile_pic: response.data.profile_pic,
        blood_group: response.data.blood_group,
        status: response.data.status,
        subscription_status: response.data.subscription_status,
        confirm_status: response.data.confirm_status,
        kyc_status: response.data.kyc_status,
        address: response.data.address,
        city: response.data.city,
        zip_code: response.data.zip_code,
        user_type: 'Technician',
      };
      localStorage.setItem('technician_token', token);
      localStorage.setItem('technician_info', JSON.stringify(userInfo));
    }
    
    // Emit login event
    eventBus.emit('userLoggedIn', { 
      isLoggedIn: true, 
      userInfo, 
      userType: type 
    });
    
    // ADD THIS - Set success message
    setStatusMessage(response.message || 'Login successful.');
    
    // ADD THIS - Navigate after a small delay
    setTimeout(() => {
      const returnTo = location.state?.from;
      let destination = type === 'user' ? '/profile' : '/tech-dashboard';
      if (type === 'user' && typeof returnTo === 'string' && returnTo.startsWith('/tech-profile/')) {
        destination = returnTo;
      }
      navigate(destination);
    }, 500);
    
    return true;
  }
}else{
  alert(response.message || 'Login failed. Please try again.');
  navigate(type === 'user' ? '/register' : '/login',{state:{mobile:mobile,requester:type==='tech'?'Technician':'User'}});
}

  
    } catch (error) {
      setApiError(error.message || 'Network error while logging in.');
      setStatusMessage('');
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (activeTab === 'user') {
      if (userStep === 'phone') {
        await handleSendOtp(e);
      } else if (userStep === 'otp') {
        await handleVerifyOtp();
      }
    } else if (activeTab === 'tech') {
      if (techStep === 'phone') {
        await handleSendOtp(e);
      } else if (techStep === 'otp') {
        await handleVerifyOtp();
      }
    }
  };

  const handleResendOtp = async (e) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    const mobile = getPhoneValue();
    const requester = activeTab === 'tech' ? 'Technician' : 'User';

    if (!/^[0-9]{10}$/.test(mobile)) {
      setApiError('Invalid phone number.');
      alert('Please enter a valid 10‑digit mobile number before resending OTP.');
      return;
    }

    try {
      setApiError('');
      setStatusMessage('Resending OTP...');
      const response = await sendOtp(mobile, requester);

      if (response.status === 200 && (response.success === true || response.otp)) {
        setStatusMessage('OTP resent successfully.');
        if (activeTab === 'user') {
          setUserOtp(['', '', '', '']);
        } else {
          setTechOtp(['', '', '', '']);
        }
      } else {
        setApiError(response.message || 'Unable to resend OTP.');
        setStatusMessage('');
      }
    } catch (error) {
      setApiError(error.message || 'Network error while resending OTP.');
      setStatusMessage('');
    }
  };

  useEffect(() => {
    setApiError('');
    setStatusMessage('');
  }, [activeTab]);

  if (authenticatedDestination) return null;

  return (
    <div className="authflowContainer">
      <div className="authflowWrapper">
        {/* Left Side - Branding/Design Section */}
        <div className="authflowLeftPanel">
          <div className="authflowBrandContent">
            <div className="authflowLogoBadge">
              <span className="authflowLogoIcon">✦</span>
              <span>Secure Access</span>
            </div>
            
            <h1 className="authflowMainTitle">
              Welcome to<br />
              <span className="authflowGradientText">One Click</span>
            </h1>
            
            <p className="authflowDescription">
              Secure authentication with OTP verification. 
              Experience seamless login for both users and technicians.
            </p>
            
            <div className="authflowFeatureGrid">
              <div className="authflowFeatureItem">
                <div className="authflowFeatureIcon">🔐</div>
                <div className="authflowFeatureText">
                  <strong>Secure OTP</strong>
                  <p>One-time password verification</p>
                </div>
              </div>
              
              <div className="authflowFeatureItem">
                <div className="authflowFeatureIcon">⚡</div>
                <div className="authflowFeatureText">
                  <strong>Fast Access</strong>
                  <p>Instant authentication</p>
                </div>
              </div>
              
              <div className="authflowFeatureItem">
                <div className="authflowFeatureIcon">🛡️</div>
                <div className="authflowFeatureText">
                  <strong>Protected</strong>
                  <p>End-to-end encrypted</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="authflowDecorationCircle"></div>
          <div className="authflowDecorationCircle2"></div>
        </div>

        {/* Right Side - Login Forms */}
        <div className="authflowRightPanel">
          {/* Toggle Buttons */}
          <div className="authflowToggleContainer">
            <button
              type="button"
              className={`authflowToggleBtn ${activeTab === 'user' ? 'authflowActiveToggle' : ''}`}
              onClick={() => {
                setActiveTab('user');
                setUserStep('phone');
                setUserOtp(['', '', '', '']);
              }}
            >
              User Login
            </button>
            <button
              type="button"
              className={`authflowToggleBtn ${activeTab === 'tech' ? 'authflowActiveToggle' : ''}`}
              onClick={() => {
                setActiveTab('tech');
                setTechStep('phone');
                setTechOtp(['', '', '', '']);
              }}
            >
              Technician Login
            </button>
            <div
              className="authflowToggleSlider"
              style={{ transform: activeTab === 'tech' ? 'translateX(100%)' : 'translateX(0)' }}
            ></div>
          </div>

          {/* Forms Container */}
          <div className="authflowFormsContainer">
            {/* User Login Form */}
            <form
              className={`authflowForm ${activeTab === 'user' ? 'authflowFormActive' : ''}`}
              onSubmit={handleSubmit}
            >
              <h2 className="authflowFormTitle">
                {userStep === 'phone' ? 'Sign in with OTP' : 'Verify Your Code'}
              </h2>
              
              <p className="authflowFormSubtitle">
                {userStep === 'phone' 
                  ? 'Enter your mobile number to receive a verification code' 
                  : `We've sent a 4-digit code to ${mobileNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}`}
              </p>

              {/* Phone Step */}
              <div className={`authflowStep ${userStep === 'phone' ? 'authflowStepActive' : ''}`}>
                <div className="authflowInputGroup">
                  <input
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    required={activeTab === 'user'}
                    disabled={userStep === 'otp'}
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    className="authflowInput"
                  />
                  <button
                    type="button"
                    className="authflowPrimaryBtn"
                    onClick={(e) => handleSendOtp(e)}
                    disabled={userStep === 'otp'}
                  >
                    Send OTP
                  </button>
                </div>
              </div>

              {/* OTP Step */}
              <div className={`authflowStep ${userStep === 'otp' ? 'authflowStepActive' : ''}`}>
                <div className="authflowOtpContainer">
                  {userOtp.map((digit, index) => (
                    <input
                      key={`user-otp-${index}`}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value, 'user')}
                      onKeyDown={(e) => handleKeyDown(e, index, 'user')}
                      ref={(el) => userInputRefs.current[index] = el}
                      className="authflowOtpInput"
                      placeholder="0"
                    />
                  ))}
                </div>
                
                <button type="submit" className="authflowPrimaryBtn">
                  Verify & Login
                </button>
                
                <button
                  type="button"
                  className="authflowSecondaryBtn"
                  onClick={(e) => handleResendOtp(e)}
                >
                  Resend OTP
                </button>
              </div>

              {/* Messages */}
              {statusMessage && activeTab === 'user' && (
                <div className="authflowStatusMsg">{statusMessage}</div>
              )}
              {apiError && activeTab === 'user' && (
                <div className="authflowErrorMsg">{apiError}</div>
              )}
            </form>

            {/* Technician Login Form */}
            <form
              className={`authflowForm ${activeTab !== 'user' ? 'authflowFormActive' : ''}`}
              onSubmit={handleSubmit}
            >
              <h2 className="authflowFormTitle">
                {techStep === 'phone' ? 'Technician Access' : 'Verify Technician Code'}
              </h2>
              
              <p className="authflowFormSubtitle">
                {techStep === 'phone' 
                  ? 'Authorized technicians enter your registered mobile number' 
                  : `Verification code sent to ${mobileNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}`}
              </p>

              {/* Phone Step */}
              <div className={`authflowStep ${techStep === 'phone' ? 'authflowStepActive' : ''}`}>
                <div className="authflowInputGroup">
                  <input
                    type="tel"
                    placeholder="Enter 10-digit mobile number"
                    required={activeTab === 'tech'}
                    disabled={techStep === 'otp'}
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    className="authflowInput"
                  />
                  <button
                    type="button"
                    className="authflowPrimaryBtn"
                    onClick={(e) => handleSendOtp(e)}
                    disabled={techStep === 'otp'}
                  >
                    Send OTP
                  </button>
                </div>
              </div>

              {/* OTP Step */}
              <div className={`authflowStep ${techStep === 'otp' ? 'authflowStepActive' : ''}`}>
                <div className="authflowOtpContainer">
                  {techOtp.map((digit, index) => (
                    <input
                      key={`tech-otp-${index}`}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value, 'tech')}
                      onKeyDown={(e) => handleKeyDown(e, index, 'tech')}
                      ref={(el) => techInputRefs.current[index] = el}
                      className="authflowOtpInput"
                      placeholder="0"
                    />
                  ))}
                </div>
                
                <button type="submit" className="authflowPrimaryBtn">
                  Verify & Login
                </button>
                
                <button
                  type="button"
                  className="authflowSecondaryBtn"
                  onClick={(e) => handleResendOtp(e)}
                >
                  Resend OTP
                </button>
              </div>

              {/* Messages */}
              {statusMessage && activeTab === 'tech' && (
                <div className="authflowStatusMsg">{statusMessage}</div>
              )}
              {apiError && activeTab === 'tech' && (
                <div className="authflowErrorMsg">{apiError}</div>
              )}
            </form>
          </div>
        </div>
      </div>

      <style>{`
        /* Unique classname styling - white background, black text, animations */
        .authflowContainer {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
        }

        .authflowWrapper {
          max-width: 1280px;
          width: 100%;
          background: #FFFFFF;
          border-radius: 32px;
          display: flex;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
          animation: authflowFadeIn 0.5s ease-out;
        }

        @keyframes authflowFadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Left Panel */
        .authflowLeftPanel {
          flex: 1;
          background: #FFFFFF;
          padding: 48px 40px;
          position: relative;
          overflow: hidden;
          border-right: 1px solid #f0f0f0;
        }

        .authflowBrandContent {
          position: relative;
          z-index: 2;
        }

        .authflowLogoBadge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: #f8f8f8;
          padding: 8px 16px;
          border-radius: 40px;
          font-size: 0.875rem;
          font-weight: 500;
          color: #000;
          margin-bottom: 48px;
          border: 1px solid #eaeaea;
        }

        .authflowLogoIcon {
          font-size: 1.2rem;
        }

        .authflowMainTitle {
          font-size: 3rem;
          font-weight: 700;
          line-height: 1.2;
          color: #000;
          margin-bottom: 20px;
          letter-spacing: -0.02em;
        }

        .authflowGradientText {
          background: linear-gradient(135deg, #000000 0%, #333333 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }

        .authflowDescription {
          color: #666;
          line-height: 1.6;
          margin-bottom: 40px;
          font-size: 0.95rem;
        }

        .authflowFeatureGrid {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .authflowFeatureItem {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .authflowFeatureIcon {
          width: 48px;
          height: 48px;
          background: #fafafa;
          border-radius: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          border: 1px solid #f0f0f0;
        }

        .authflowFeatureText strong {
          display: block;
          color: #000;
          margin-bottom: 4px;
          font-size: 0.95rem;
        }

        .authflowFeatureText p {
          color: #888;
          font-size: 0.8rem;
          margin: 0;
        }

        .authflowDecorationCircle {
          position: absolute;
          width: 300px;
          height: 300px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0) 70%);
          bottom: -100px;
          right: -100px;
          animation: authflowPulse 8s ease-in-out infinite;
        }

        .authflowDecorationCircle2 {
          position: absolute;
          width: 200px;
          height: 200px;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(0,0,0,0.01) 0%, rgba(0,0,0,0) 70%);
          top: -50px;
          left: -50px;
          animation: authflowPulse 6s ease-in-out infinite reverse;
        }

        @keyframes authflowPulse {
          0%, 100% { transform: scale(1); opacity: 0.5; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }

        /* Right Panel */
        .authflowRightPanel {
          flex: 1;
          padding: 48px 40px;
          background: #FFFFFF;
          display: flex;
          flex-direction: column;
        }

        /* Toggle */
        .authflowToggleContainer {
          background: #f5f5f5;
          border-radius: 60px;
          display: flex;
          position: relative;
          margin-bottom: 40px;
          padding: 4px;
        }

        .authflowToggleBtn {
          flex: 1;
          text-align: center;
          padding: 12px 24px;
          background: transparent;
          border: none;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          position: relative;
          z-index: 2;
          transition: color 0.3s ease;
          color: #666;
          border-radius: 60px;
        }

        .authflowActiveToggle {
          color: #FFFFFF !important;
        }

        .authflowToggleSlider {
          position: absolute;
          top: 4px;
          bottom: 4px;
          width: calc(50% - 4px);
          background: #000000;
          border-radius: 60px;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
        }

        /* Forms */
        .authflowFormsContainer {
          position: relative;
          flex: 1;
        }

        .authflowForm {
          display: none;
          opacity: 1;
          transition: opacity 0.3s ease;
        }

        .authflowFormActive {
          display: block;
          animation: authflowSlideIn 0.4s ease-out;
        }

        @keyframes authflowSlideIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .authflowFormTitle {
          font-size: 1.75rem;
          font-weight: 700;
          color: #000;
          margin-bottom: 12px;
          letter-spacing: -0.3px;
        }

        .authflowFormSubtitle {
          color: #888;
          font-size: 0.9rem;
          margin-bottom: 32px;
          line-height: 1.5;
        }

        /* Steps */
        .authflowStep {
          display: none;
          flex-direction: column;
          gap: 24px;
        }

        .authflowStepActive {
          display: flex;
          animation: authflowFadeUp 0.4s ease-out;
        }

        @keyframes authflowFadeUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .authflowInputGroup {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .authflowInput {
          width: 100%;
          padding: 16px 20px;
          font-size: 1rem;
          border: 2px solid #e8e8e8;
          border-radius: 16px;
          background: #FFFFFF;
          transition: all 0.3s ease;
          outline: none;
          color: #000;
          font-weight: 500;
        }

        .authflowInput:focus {
          border-color: #000;
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
        }

        .authflowInput::placeholder {
          color: #bbb;
        }

        .authflowPrimaryBtn {
          background: #000000;
          color: #FFFFFF;
          border: none;
          padding: 16px 24px;
          border-radius: 16px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .authflowPrimaryBtn:hover {
          background: #222;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        .authflowPrimaryBtn:disabled {
          background: #ccc;
          cursor: not-allowed;
          transform: none;
        }

        .authflowSecondaryBtn {
          background: transparent;
          color: #000;
          border: 2px solid #e8e8e8;
          padding: 14px 24px;
          border-radius: 16px;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .authflowSecondaryBtn:hover {
          border-color: #000;
          background: #fafafa;
        }

        /* OTP Inputs */
        .authflowOtpContainer {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 8px;
        }

        .authflowOtpInput {
          width: 70px;
          height: 70px;
          text-align: center;
          font-size: 1.75rem;
          font-weight: 600;
          border: 2px solid #e8e8e8;
          border-radius: 16px;
          background: #FFFFFF;
          transition: all 0.2s ease;
          outline: none;
          color: #000;
        }

        .authflowOtpInput:focus {
          border-color: #000;
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
          transform: scale(1.02);
        }

        /* Messages */
        .authflowStatusMsg {
          margin-top: 20px;
          padding: 12px;
          background: #f0f9ff;
          border-left: 4px solid #000;
          color: #000;
          border-radius: 8px;
          font-size: 0.9rem;
          animation: authflowShake 0.3s ease;
        }

        .authflowErrorMsg {
          margin-top: 20px;
          padding: 12px;
          background: #fff5f5;
          border-left: 4px solid #ff0000;
          color: #cc0000;
          border-radius: 8px;
          font-size: 0.9rem;
          animation: authflowShake 0.3s ease;
        }

        @keyframes authflowShake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        /* Responsive */
        @media (max-width: 968px) {
          .authflowWrapper {
            flex-direction: column;
            max-width: 500px;
          }
          
          .authflowLeftPanel {
            border-right: none;
            border-bottom: 1px solid #f0f0f0;
            padding: 32px 28px;
          }
          
          .authflowRightPanel {
            padding: 32px 28px;
          }
          
          .authflowMainTitle {
            font-size: 2rem;
          }
          
          .authflowOtpInput {
            width: 55px;
            height: 55px;
            font-size: 1.5rem;
          }
        }

        @media (max-width: 480px) {
          .authflowOtpContainer {
            gap: 8px;
          }
          
          .authflowOtpInput {
            width: 45px;
            height: 45px;
            font-size: 1.25rem;
          }
          
          .authflowToggleBtn {
            padding: 10px 16px;
            font-size: 0.85rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;